"use client";

import { useAuth } from "@/contexts/auth-context";
import { EventType, publishEvent } from "@/events";
import { fireUserEngagementEvent } from "@/libs/utils";
import { LogOut, ShoppingBag } from "lucide-react";
import { useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";

declare global {
  interface Window {
    __KP_LOGIN_SDK_INSTANCE__?: {
      handleKpLogin: () => void;
    };
  }
}

/**
 * KwikPass logo SVG component
 */
export const KwikPassLogo = ({
  width = "24",
  height = "24",
  currentColor = "black",
}) => {
  return (
    <svg
      width={width}
      height={height}
      className="cursor-pointer hover:opacity-80 transition-opacity w-[25px] h-[25px] md:w-[30px] md:h-[30px]"
      viewBox="0 0 25 25"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M22.9129 12.935L13.7571 23.0474C13.5348 23.2929 13.1284 23.1084 13.1669 22.7794L14.0816 14.9731H10.6991C10.4034 14.9731 10.2484 14.6219 10.4478 14.4035L20.3133 3.59739C20.5589 3.32834 20.9984 3.58134 20.8891 3.92887L18.2354 12.3664H22.6607C22.9557 12.3664 23.1109 12.7163 22.9129 12.935Z"
        fill="#FEA203"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M16.6079 5.35819C16.4805 5.1933 16.3421 5.03582 16.1932 4.8869C15.2702 3.96387 14.0183 3.44531 12.7129 3.44531C11.4075 3.44531 10.1556 3.96387 9.2326 4.8869C8.30957 5.80993 7.79102 7.06183 7.79102 8.36719C7.79102 9.67255 8.30957 10.9244 9.2326 11.8475C9.48368 12.0986 9.75909 12.3197 10.0533 12.5086L11.0235 11.4503C10.7335 11.2914 10.4649 11.0911 10.227 10.8531C9.56766 10.1938 9.19727 9.29959 9.19727 8.36719C9.19727 7.43479 9.56766 6.54057 10.227 5.88127C10.8863 5.22196 11.7805 4.85156 12.7129 4.85156C13.6453 4.85156 14.5395 5.22196 15.1988 5.88127C15.3636 6.04604 15.5103 6.22549 15.6377 6.41654L16.6079 5.35819ZM20.6413 18.6497L19.6746 19.7132C20.1676 20.4122 20.4473 21.2264 20.4473 22.0781V23.8359C20.4473 24.2243 20.7621 24.5391 21.1504 24.5391C21.5387 24.5391 21.8535 24.2243 21.8535 23.8359V22.0781C21.8535 20.7863 21.4016 19.6103 20.6413 18.6497ZM12.3111 17.5078H10.3026C7.27113 17.5078 4.97852 19.6394 4.97852 22.0781V23.8359C4.97852 24.2243 4.66372 24.5391 4.27539 24.5391C3.88707 24.5391 3.57227 24.2243 3.57227 23.8359V22.0781C3.57227 18.6922 6.67684 16.1016 10.3026 16.1016H12.4885L12.3111 17.5078Z"
        fill={currentColor}
        stroke={currentColor}
      />
    </svg>
  );
};

const Close = ({ currentColor = "black" }) => {
  return (
    <svg
      width="25"
      height="25"
      viewBox="0 0 25 25"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="border-2 border-[#840000] rounded-full w-[32px] h-[32px] md:w-[32px] md:h-[32px]"
    >
      <path
        d="M18 6L6 18M6 6L18 18"
        stroke={currentColor}
        strokeWidth="1.66667"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

/**
 * KwikpassLogin component that handles user authentication via KwikPass
 * Displays different UI based on login state
 */
export function KwikpassLogin() {
  const [open, setOpen] = useState(false);
  const { accessToken, logout } = useAuth();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const router = useRouter();

  /**
   * Handles user logout
   */
  const doLogout = () => {
    try {
      if (typeof window !== "undefined" && window.handleLogout) {
        window.handleLogout("/");
      }
      fireUserEngagementEvent();
      logout();
      setOpen(false);
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  /**
   * Triggers KwikPass login flow
   */
  const handleLogin = () => {
    try {
      if (typeof window !== "undefined") {
        if (window.__KP_LOGIN_SDK_INSTANCE__) {
          window.__KP_LOGIN_SDK_INSTANCE__.handleKpLogin();
          setOpen(false);
          fireUserEngagementEvent();

          // Track login attempt event
          publishEvent({
            type: EventType.CUSTOMER_LOGGED_IN,
            userId: "",
            method: "kwikpass",
          });
        }
      }
    } catch (error) {
      console.error("Login failed:", error);
    }
  };

  /**
   * Handle click outside to close dropdown
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  return (
    <div className="relative" data-test-id="kwikpass-login">
      {accessToken ? (
        open ? (
          <button
            id="user-account"
            ref={buttonRef}
            className="text-gray-800"
            aria-label="User Account"
            onClick={() => setOpen(false)}
            data-test-id="toggle-user-dropdown"
          >
            <Close currentColor="#840000" />
          </button>
        ) : (
          <button
            id="KwikPassLogo"
            ref={buttonRef}
            className="text-gray-800"
            aria-label="User Account"
            onClick={() => setOpen(true)}
          >
            <div className="rounded-full border-2 border-[#840000] p-0.5">
              <KwikPassLogo height="15" width="15" currentColor="#840000" />
            </div>
          </button>
        )
      ) : (
        <button
          className="text-gray-800"
          aria-label="Login"
          onClick={handleLogin}
          id="login"
          data-test-id="login-button"
        >
          <div className="rounded-full border-2 border-[#840000] p-0.5">
            <KwikPassLogo height="15" width="15" currentColor="#840000" />
          </div>
        </button>
      )}

      {open && accessToken && (
        <div
          ref={dropdownRef}
          className="p-4 overflow-hidden rounded-b-lg border border-gray-100 absolute right-0 z-50 bg-white top-9"
          style={{
            boxShadow: "0px 0px 32px 0px rgba(16, 24, 40, 0.32)",
            width: 172,
          }}
          data-test-id="user-dropdown"
        >
          <div className="flex flex-col space-y-4">
            <button
              id="order-history"
              className="flex items-center font-medium text-gray-800 hover:text-gray-600"
              onClick={() => {
                fireUserEngagementEvent();
                router.push("/account/order-history");
                setOpen(false);
              }}
              data-test-id="order-history-button"
            >
              <ShoppingBag className="w-4 h-4 mr-2" />
              Order History
            </button>

            <button
              id="logout"
              className="flex items-center justify-center w-full p-2 border border-gray-300 rounded text-gray-700 font-medium hover:bg-gray-50"
              aria-label="Logout"
              onClick={doLogout}
              data-test-id="logout-button"
            >
              Logout
              <LogOut className="ml-2 h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
